/**
 * Service layer for content-service generation runs. Thin typed wrappers over
 * `contentClient` + `endpoints.content`.
 *
 * Two things every function here has in common:
 * - The service wraps everything in `{success, message, data}`, so each read
 *   unwraps `data` and the caller never sees the envelope.
 * - Tenancy is the interceptor's job — `X-Subsidiary-ID` is injected by
 *   `subsidiaryInterceptor` and cross-checked server-side against the JWT.
 */
import { contentClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  BatchRead,
  ContentHealthRead,
  ContentPage,
  ContentPaginatedResponse,
  ContentResponse,
  GeneratedContentRead,
  GenerationRunCreate,
  GenerationRunDetail,
  GenerationRunRead,
  ItemFailureRead,
  RunAction,
  RunActionRead,
  RunBulkArchiveRead,
  RunBatchFilters,
  RunContentFilters,
  RunListFilters,
  RunMetricsRead,
} from '../types/content.types';
import type { FlowSpecRead } from '../types/model-configuration.types';
import type {
  BatchDetailRead,
  BatchProductFilters,
  BatchProductPage,
  BatchProductRead,
  RunProductCountsRead,
  RunProductFilters,
  RunProductRead,
  RunStagesRead,
} from '../types/run-progress.types';

/**
 * Ky's `searchParams` takes no arrays, so repeatable filters (`?status=A&status=B`)
 * are built as `URLSearchParams` by hand. Empty values are skipped rather than
 * sent blank — the service validates enums and a `?status=` would be a 422.
 */
export function toSearchParams(
  entries: Record<string, string | number | undefined>,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(entries)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  return params;
}

/** Appends one repeatable key once per value. */
export function appendAll(params: URLSearchParams, key: string, values?: readonly string[]): void {
  for (const value of values ?? []) params.append(key, value);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * One keyset page of this subsidiary's runs, newest first.
 *
 * Returns the cursor alongside the rows because the envelope's `total` is
 * always 0 on keyset endpoints — there is no page count to page with, only
 * "is there more".
 */
export async function listContentRuns(
  filters: RunListFilters = {},
  cursor?: string,
): Promise<ContentPage<GenerationRunRead>> {
  const params = toSearchParams({ limit: filters.limit ?? 50, cursor });
  appendAll(params, 'status', filters.statuses);
  appendAll(params, 'origin', filters.origins);
  appendAll(params, 'kind', filters.kinds);
  appendAll(params, 'platform', filters.platforms);
  if (filters.createdAfter) params.set('createdAfter', filters.createdAfter);
  // EXCLUDE is the service default; only a non-default choice is sent.
  if (filters.archived && filters.archived !== 'EXCLUDE') params.set('archived', filters.archived);

  const response = await contentClient
    .get(endpoints.content.runs.list(), { searchParams: params })
    .json<ContentPaginatedResponse<GenerationRunRead>>();

  return { items: response.data, nextCursor: response.pagination.nextCursor };
}

/** The stored run row plus the workflow's live progress while it is in flight. */
export async function getContentRunDetail(runId: string): Promise<GenerationRunDetail | null> {
  const response = await contentClient
    .get(endpoints.content.runs.detail(runId))
    .json<ContentResponse<GenerationRunDetail>>();
  return response.data;
}

/** A run's batches, ascending by index — which is execution order. */
export async function listRunBatches(
  runId: string,
  filters: RunBatchFilters = {},
  cursor?: string,
): Promise<ContentPage<BatchRead>> {
  const params = toSearchParams({ limit: filters.limit ?? 100, cursor });
  appendAll(params, 'status', filters.statuses);

  const response = await contentClient
    .get(endpoints.content.runs.batches(runId), { searchParams: params })
    .json<ContentPaginatedResponse<BatchRead>>();

  return { items: response.data, nextCursor: response.pagination.nextCursor };
}

/**
 * A run's generated content, de-duplicated to the newest generation of each
 * `(productId, field)`.
 *
 * `product_id` is snake_case ON PURPOSE and must stay that way: the service
 * declares that filter in snake_case to match what the CLI and MCP tool already
 * send, and FastAPI drops an undeclared query param silently — a camelCase
 * `productId` here would look implemented while filtering nothing.
 */
export async function listRunContent(
  runId: string,
  filters: RunContentFilters = {},
  cursor?: string,
): Promise<ContentPage<GeneratedContentRead>> {
  const params = toSearchParams({
    limit: filters.limit ?? 100,
    cursor,
    product_id: filters.productId,
  });
  appendAll(params, 'field', filters.fields);

  const response = await contentClient
    .get(endpoints.content.runs.content(runId), { searchParams: params })
    .json<ContentPaginatedResponse<GeneratedContentRead>>();

  return { items: response.data, nextCursor: response.pagination.nextCursor };
}

/** Every failed (product, field) attempt. Retries are retained, not collapsed. */
export async function listRunFailures(
  runId: string,
  limit = 100,
  cursor?: string,
): Promise<ContentPage<ItemFailureRead>> {
  const response = await contentClient
    .get(endpoints.content.runs.failures(runId), {
      searchParams: toSearchParams({ limit, cursor }),
    })
    .json<ContentPaginatedResponse<ItemFailureRead>>();

  return { items: response.data, nextCursor: response.pagination.nextCursor };
}

/** Batch, failure and token aggregates computed from the rows the run produced. */
export async function getRunMetrics(runId: string): Promise<RunMetricsRead | null> {
  const response = await contentClient
    .get(endpoints.content.runs.metrics(runId))
    .json<ContentResponse<RunMetricsRead>>();
  return response.data;
}

/** Progress per (platform, flow, phase) plus the run's batches by status. Stored rows only. */
export async function getRunStages(runId: string): Promise<RunStagesRead | null> {
  const response = await contentClient
    .get(endpoints.content.runs.stages(runId))
    .json<ContentResponse<RunStagesRead>>();
  return response.data;
}

/** One batch with its stages and sub-batches; a RUNNING batch adds the live stage view. */
export async function getRunBatch(
  runId: string,
  batchIndex: number,
): Promise<BatchDetailRead | null> {
  const response = await contentClient
    .get(endpoints.content.runs.batch(runId, batchIndex))
    .json<ContentResponse<BatchDetailRead>>();
  return response.data;
}

/**
 * One page of the products a batch fetched (page/size, real total). `outcome` is null until the
 * batch closes — read it against `batchStatus` to tell "in flight" from "never recorded".
 */
export async function listBatchProducts(
  runId: string,
  batchIndex: number,
  filters: BatchProductFilters = {},
): Promise<BatchProductPage> {
  const response = await contentClient
    .get(endpoints.content.runs.batchProducts(runId, batchIndex), {
      searchParams: toSearchParams({
        platform: filters.platform,
        page: filters.page ?? 1,
        size: filters.size ?? 50,
      }),
    })
    .json<ContentPaginatedResponse<BatchProductRead>>();
  return { items: response.data, total: response.pagination.total };
}

/** The `platform` + repeatable `vertical` / `status` params the products list and counts share. */
function runProductParams(
  filters: RunProductFilters,
  extra: Record<string, string | number | undefined>,
) {
  const params = toSearchParams({ platform: filters.platform, ...extra });
  appendAll(params, 'vertical', filters.verticals);
  appendAll(params, 'status', filters.statuses);
  return params;
}

/** One keyset page of what the run did to each product, in batch then product order. */
export async function listRunProducts(
  runId: string,
  filters: RunProductFilters = {},
  cursor?: string,
): Promise<ContentPage<RunProductRead>> {
  const response = await contentClient
    .get(endpoints.content.runs.products(runId), {
      searchParams: runProductParams(filters, { limit: filters.limit ?? 100, cursor }),
    })
    .json<ContentPaginatedResponse<RunProductRead>>();
  return { items: response.data, nextCursor: response.pagination.nextCursor };
}

/** The run's products counted by (vertical, status) under the same filters as the list. */
export async function getRunProductCounts(
  runId: string,
  filters: RunProductFilters = {},
): Promise<RunProductCountsRead | null> {
  const response = await contentClient
    .get(endpoints.content.runs.productCounts(runId), {
      searchParams: runProductParams(filters, {}),
    })
    .json<ContentResponse<RunProductCountsRead>>();
  return response.data;
}

/** The static flow catalog — what a run can be asked to do. */
export async function listContentFlows(): Promise<FlowSpecRead[]> {
  const response = await contentClient
    .get(endpoints.content.modelConfiguration.flows())
    .json<ContentResponse<FlowSpecRead[]>>();
  return response.data ?? [];
}

/** Readiness — reports ClickHouse and Temporal, and 503s when either is down. */
export async function getContentHealth(): Promise<ContentHealthRead | null> {
  const response = await contentClient
    .get(endpoints.content.health(), {
      // A degraded service answers 503 with a full body naming the failed
      // dependency. That IS the answer we want to render, so don't let Ky
      // turn it into a thrown error and hide it.
      throwHttpErrors: false,
    })
    .json<ContentResponse<ContentHealthRead>>();
  return response.data;
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Starts a run: writes the row, then starts the parent workflow. 201. */
export async function createContentRun(payload: GenerationRunCreate): Promise<GenerationRunDetail> {
  const response = await contentClient
    .post(endpoints.content.runs.create(), { json: payload })
    .json<ContentResponse<GenerationRunDetail>>();

  if (!response.data) throw new Error(response.message || 'Run was not started');
  return response.data;
}

/**
 * Delivers a pause/resume/cancel signal to the run's workflow.
 *
 * The returned `status` is the run's status when the signal was ACCEPTED, not
 * after the transition — the workflow moves it asynchronously, so the caller
 * re-reads the detail rather than trusting this.
 */
export async function signalContentRun(
  runId: string,
  action: RunAction,
): Promise<RunActionRead | null> {
  const path = {
    pause: endpoints.content.runs.pause,
    resume: endpoints.content.runs.resume,
    cancel: endpoints.content.runs.cancel,
  }[action](runId);

  const response = await contentClient.post(path).json<ContentResponse<RunActionRead>>();
  return response.data;
}

/** Most runs one bulk archive request takes (the service's bound). */
export const ARCHIVE_BULK_MAX = 500;

/**
 * Archives (or unarchives) runs in one `POST /runs/archive` per 500 ids: one outcome per id,
 * never a 409/404 for a single run — a running run answers NOT_FINISHED and is left as it is.
 */
export async function setRunsArchived(
  runIds: readonly string[],
  archived: boolean,
  reason?: string,
): Promise<RunBulkArchiveRead> {
  const out: RunBulkArchiveRead = { results: [], changed: 0, unchanged: 0, refused: 0 };
  for (let i = 0; i < runIds.length; i += ARCHIVE_BULK_MAX) {
    const response = await contentClient
      .post(endpoints.content.runs.archiveBulk(), {
        json: {
          runIds: runIds.slice(i, i + ARCHIVE_BULK_MAX),
          archived,
          ...(reason?.trim() && { reason: reason.trim() }),
        },
      })
      .json<ContentResponse<RunBulkArchiveRead>>();
    if (!response.data) throw new Error(response.message || 'The archive answer was empty');
    out.results.push(...response.data.results);
    out.changed += response.data.changed;
    out.unchanged += response.data.unchanged;
    out.refused += response.data.refused;
  }
  return out;
}
